import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User } from '../types';
import { login, logout, getCurrentUser } from '../services/authService';
import { supabase } from '../services/db';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => {},
  logout: async () => {},
  setUser: () => {},
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [hasSession, setHasSession] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    console.log('🚀 AuthProvider: Starting initialization');

    const initializeAuth = async () => {
      try {
        console.log('🔍 AuthProvider: Checking Supabase session');
        const { data: { session } } = await supabase.auth.getSession();

        if (session) {
          console.log('✅ AuthProvider: Valid session found, loading user');
          setHasSession(true);
          const currentUser = await getCurrentUser();
          if (currentUser) {
            console.log('✅ AuthProvider: User found during initialization:', currentUser.email);
            console.log('🔍 AuthProvider: Initial user isDisabled status:', currentUser.isDisabled);
            setUser(currentUser);
            localStorage.setItem('currentUser', JSON.stringify(currentUser));
          } else {
            console.log('ℹ️ AuthProvider: Session exists but no user profile found');
            setUser(null);
            setHasSession(false);
          }
        } else {
          console.log('ℹ️ AuthProvider: No session found during initialization');
          setUser(null);
          setHasSession(false);
          localStorage.removeItem('currentUser');
        }
      } catch (err) {
        console.error('Error initializing auth:', err);
        setError('Error initializing authentication');
      } finally {
        console.log('🏁 AuthProvider: Initialization complete, setting isLoading to false');
        setIsLoading(false);
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔔 AuthProvider: Auth state changed:', event, !!session);

      if (event === 'SIGNED_OUT') {
        console.log('👋 AuthProvider: User signed out via auth event');
        setUser(null);
        setHasSession(false);
        localStorage.removeItem('currentUser');
        return;
      }

      if (
        (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') &&
        session
      ) {
        setHasSession(true);
        (async () => {
          try {
            const freshUser = await getCurrentUser();
            if (freshUser) {
              console.log('✅ AuthProvider: User refreshed after auth event:', freshUser.email);
              console.log('🔍 AuthProvider: Refreshed user isDisabled status:', freshUser.isDisabled);
              setUser(freshUser);
              localStorage.setItem('currentUser', JSON.stringify(freshUser));
            } else {
              console.log('ℹ️ AuthProvider: No user profile found after auth event');
              setUser(null);
              setHasSession(false);
            }
          } catch (err) {
            console.error('Error refreshing user after auth event:', err);
          }
        })();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);


  const handleLogin = async (email: string, password: string) => {
    try {
      console.log('🔐 AuthProvider: Starting login for:', email);
      const userData = await login(email, password);
      console.log('✅ AuthProvider: Login successful for:', userData.email);
      console.log('🔍 AuthProvider: User isDisabled status after login:', userData.isDisabled);
      setUser(userData);
      setHasSession(true);
      localStorage.setItem('currentUser', JSON.stringify(userData));
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    }
  };

  const handleLogout = async () => {
    try {
      console.log('👋 AuthProvider: Starting logout');
      await logout();
      localStorage.removeItem('currentUser');
      setUser(null);
      setHasSession(false);
    } catch (err) {
      console.error('Logout error:', err);
      throw err;
    }
  };

  console.log('🎭 AuthProvider: Rendering with state:', {
    isLoading,
    hasUser: !!user,
    hasSession,
    userEmail: user?.email,
    isDisabled: user?.isDisabled,
    error
  });

  if (isLoading) {
    console.log('⏳ AuthProvider: Showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    console.log('❌ AuthProvider: Showing error state:', error);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-red-600 text-center">
          <p>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isAuthenticated = hasSession && !!user;

  console.log('✅ AuthProvider: Rendering children with user:', user?.email || 'no user', 'isAuthenticated:', isAuthenticated);
  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login: handleLogin, logout: handleLogout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};
